/**
 * Currency Utilities
 * Helper functions for multi-currency support
 */

export type SupportedCurrency = 'USD' | 'GHS' | 'NGN' | 'KES' | 'UGX' | 'ZAR' | 'EUR' | 'GBP'

export interface CurrencyInfo {
  code: SupportedCurrency
  symbol: string
  name: string
  flag: string
  locale: string
}

export const CURRENCIES: Record<SupportedCurrency, CurrencyInfo> = {
  USD: {
    code: 'USD',
    symbol: '$',
    name: 'US Dollar',
    flag: '🇺🇸',
    locale: 'en-US'
  },
  GHS: {
    code: 'GHS',
    symbol: '₵',
    name: 'Ghana Cedi',
    flag: '🇬🇭',
    locale: 'en-GH'
  },
  NGN: {
    code: 'NGN',
    symbol: '₦',
    name: 'Nigerian Naira',
    flag: '🇳🇬',
    locale: 'en-NG'
  },
  KES: {
    code: 'KES',
    symbol: 'KSh',
    name: 'Kenyan Shilling',
    flag: '🇰🇪',
    locale: 'en-KE'
  },
  UGX: {
    code: 'UGX',
    symbol: 'USh',
    name: 'Ugandan Shilling',
    flag: '🇺🇬',
    locale: 'en-UG'
  },
  ZAR: {
    code: 'ZAR',
    symbol: 'R',
    name: 'South African Rand',
    flag: '🇿🇦',
    locale: 'en-ZA'
  },
  EUR: {
    code: 'EUR',
    symbol: '€',
    name: 'Euro',
    flag: '🇪🇺',
    locale: 'en-EU'
  },
  GBP: {
    code: 'GBP',
    symbol: '£',
    name: 'British Pound',
    flag: '🇬🇧',
    locale: 'en-GB'
  }
}

/**
 * Format amount with currency symbol
 */
export function formatCurrency(amount: number, currency: SupportedCurrency): string {
  const currencyInfo = CURRENCIES[currency]

  try {
    return new Intl.NumberFormat(currencyInfo.locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)
  } catch (error) {
    // Fallback if Intl fails
    return `${currencyInfo.symbol}${amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`
  }
}

/**
 * Get currency symbol
 */
export function getCurrencySymbol(currency: SupportedCurrency): string {
  return CURRENCIES[currency]?.symbol || currency
}

/**
 * Get currency name
 */
export function getCurrencyName(currency: SupportedCurrency): string {
  return CURRENCIES[currency]?.name || currency
}

/**
 * Get currency flag emoji
 */
export function getCurrencyFlag(currency: SupportedCurrency): string {
  return CURRENCIES[currency]?.flag || ''
}

/**
 * Get full currency display (flag + code + name)
 */
export function getCurrencyDisplay(currency: SupportedCurrency): string {
  const info = CURRENCIES[currency]
  return `${info.flag} ${info.code} - ${info.name}`
}

/**
 * Format amount with currency code (e.g., "GHS 300.00")
 */
export function formatCurrencyWithCode(amount: number, currency: SupportedCurrency): string {
  return `${currency} ${amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`
}

/**
 * Approximate exchange rates to USD (for display purposes only)
 * Note: These should be updated regularly or fetched from an API
 */
export const EXCHANGE_RATES_TO_USD: Record<SupportedCurrency, number> = {
  USD: 1,
  GHS: 0.082,    // 1 GHS ≈ 0.082 USD (as of 2025)
  NGN: 0.00065,  // 1 NGN ≈ 0.00065 USD
  KES: 0.0078,   // 1 KES ≈ 0.0078 USD
  UGX: 0.00027,  // 1 UGX ≈ 0.00027 USD
  ZAR: 0.055,    // 1 ZAR ≈ 0.055 USD
  EUR: 1.10,     // 1 EUR ≈ 1.10 USD
  GBP: 1.27      // 1 GBP ≈ 1.27 USD
}

/**
 * Convert amount to USD equivalent (approximate)
 */
export function convertToUSD(amount: number, fromCurrency: SupportedCurrency): number {
  const rate = EXCHANGE_RATES_TO_USD[fromCurrency]
  return amount * rate
}

/**
 * Format as USD equivalent with original currency
 * Example: "GHS 1,220.00 (~$100.00 USD)"
 */
export function formatWithUSDEquivalent(amount: number, currency: SupportedCurrency): string {
  if (currency === 'USD') {
    return formatCurrency(amount, 'USD')
  }

  const usdEquivalent = convertToUSD(amount, currency)
  return `${formatCurrency(amount, currency)} (~${formatCurrency(usdEquivalent, 'USD')} USD)`
}
